import { enforceGridPrecisionInMeters } from "@buildwithflux/shared";
// eslint-disable-next-line import/no-named-as-default
import DxfParser from "dxf-parser";
import cloneDeep from "lodash/cloneDeep";
import { Shape, Vector2 } from "three";
// eslint-disable-next-line boundaries/element-types
import { Clipper } from "../../clipper";
import { InvalidDxfShapeAssetError } from "../../flux_error";
import { centerShapePoints } from "../helpers/centerShape";
import { decomposeArc } from "./decomposers/decomposeArc";
import { decomposeCircle } from "./decomposers/decomposeCircle";
import { decomposeEllipse } from "./decomposers/decomposeEllipse";
import { decomposeLine } from "./decomposers/decomposeLine";
import { decomposePolyline } from "./decomposers/decomposePolyline";
import { decomposeSpline } from "./decomposers/decomposeSpline"; // Lots of DXf code taken from https://github.com/vagran/dxf-viewer/blob/master/src/DxfScene.js
// Lots of DXf code taken from https://github.com/vagran/dxf-viewer/blob/master/src/DxfScene.js
// this dxf viewer was useful for debugging https://vagran.github.io/dxf-viewer-example/
/**
 * Tries to obtain scale factor from DXF files header for unified and consistent presentation of geometry in the application
 */
export function getScaleFactorFromHeader(header) {
    // For further reference, see http://images.autodesk.com/adsk/files/autocad_2012_pdf_dxf-reference_enu.pdf
    // Search: "$INSUNITS" for case definitions
    switch (Number.parseInt(`${header.$INSUNITS}`)) {
        case 0: // Unitless
            return 1;
        case 1: // Inches
            return 0.0254;
        case 2: // Feet
            return 0.3048;
        case 3: // Miles
            return 1609.344;
        case 4: // Millimeters
            return 0.001;
        case 5: // Centimeters
            return 0.01;
        case 6: // Meters
            return 1;
        case 7: // Kilometers
            return 1000;
        case 8: // Microinch
            return 2.54e-8;
        case 9: // Mils
            return 0.0000254;
        case 10: // Yards
            return 0.9144;
        case 11: // Angstroms
            return 1e-10;
        case 12: // Nanometers
            return 1e-9;
        case 13: // Microns
            return 0.000001;
        case 14: // Decimeters
            return 0.1;
        case 15: // Decameters
            return 10;
        case 16: // Hectometers
            return 100;
        case 17: // Gigameters
            return 1000000000;
        case 18: // Astronomical
            return 149597870691;
        case 19: // Lightyears
            return 9460730472580044;
        case 20: // Parsecs
            return 30856775812799588;
        default: {
            // See reference above for definition here - this is still a mystery to me
            // as to how to correctly deal with scale .. testing a range of different DXF
            // files and checking header data/format specification/online, I'm yet to find
            // the 'proper way' to deal with scale
            if (header.$MEASUREMENT === 1) {
                // Metric
                return 1;
            }
            else {
                // Assume imperial
                return 0.0;
            }
        }
    }
}
export function createShapeFromDxfString(dxfString, opts = {}) {
    let dxf = undefined;
    try {
        const parser = new DxfParser();
        dxf = parser.parse(dxfString);
    }
    catch (error) {
        throw new InvalidDxfShapeAssetError("There was an error parsing the DXF file. Please use a different file and try again.", error);
    }
    if (dxf) {
        const header = dxf.header;
        const processedEntities = dxf.entities
            .map((entity) => processDxfEntity(entity, header))
            .filter((entity) => entity);
        const orderedPaths = orderPathsByEndpoints(processedEntities);
        const combinedPaths = combinePaths(orderedPaths);
        if (combinedPaths.length !== processedEntities.flatMap((e) => e).length) {
            throw new InvalidDxfShapeAssetError("The DFX file must use closed shapes.");
        }
        const scale = opts.scale ?? new Vector2(1, 1);
        const scaleFactorFromHeader = getScaleFactorFromHeader(header);
        const points = combinedPaths.map((entityVector) => new Vector2(entityVector.x * scale.x * scaleFactorFromHeader, entityVector.y * scale.y * scaleFactorFromHeader));
        if (opts.center) {
            centerShapePoints(points);
        }
        const shape = new Shape().setFromPoints(points);
        return opts.expansion ? Clipper.fromShape(shape).offset(opts.expansion).toShape(0) : shape;
    }
    return;
}
export function combinePaths(paths) {
    return paths.flatMap((path) => path);
}
// If you need support for other shapes please see https://github.com/vagran/dxf-viewer/blob/master/src/DxfScene.js
function processDxfEntity(entity, dxfHeaders) {
    switch (entity.type) {
        case "LINE":
            return decomposeLine(entity);
        case "POLYLINE":
        case "LWPOLYLINE":
            return decomposePolyline(entity, dxfHeaders);
        case "ARC":
            return decomposeArc(entity, dxfHeaders);
        case "CIRCLE":
            return decomposeCircle(entity, dxfHeaders);
        case "ELLIPSE":
            return decomposeEllipse(entity, dxfHeaders);
        case "SPLINE":
            return decomposeSpline(entity);
        case "POINT":
            //  ignoring points since there is no way in flux to render them
            return undefined;
        default:
            throw new InvalidDxfShapeAssetError(`The DFX file contained an unsupported entity type: ${entity.type}.`);
    }
}
function areVectorsEqual(v1, v2) {
    return (enforceGridPrecisionInMeters(v1.x) === enforceGridPrecisionInMeters(v2.x) &&
        enforceGridPrecisionInMeters(v1.y) === enforceGridPrecisionInMeters(v2.y));
}
function getStartEndVectors(path) {
    return { start: path[0], end: path[path.length - 1] };
}
export function orderPathsByEndpoints(paths) {
    const pathsPoolToSearch = cloneDeep(paths);
    const result = [];
    let currentPath = paths[0];
    // put the first path into the results
    result.push(paths[0]);
    // and remove it from the pool of paths to search
    pathsPoolToSearch.splice(0, 1);
    while (result.length < paths.length) {
        const nextPathInSequence = pathsPoolToSearch.find((poolPath) => {
            const { start: poolPathStartVector, end: poolPathEndVector } = getStartEndVectors(poolPath);
            const { end: currentPathEndVector } = getStartEndVectors(currentPath);
            if (areVectorsEqual(currentPathEndVector, poolPathStartVector)) {
                return true;
            }
            if (areVectorsEqual(currentPathEndVector, poolPathEndVector)) {
                poolPath.reverse();
                return true;
            }
            return false;
        });
        if (nextPathInSequence) {
            result.push(nextPathInSequence);
            currentPath = nextPathInSequence;
            //remove found path from pool
            const pathsPoolToSearchIndex = pathsPoolToSearch.indexOf(nextPathInSequence);
            if (pathsPoolToSearchIndex > -1) {
                pathsPoolToSearch.splice(pathsPoolToSearchIndex, 1);
            }
        }
        else {
            break;
        }
    }
    return result;
}
//# sourceMappingURL=createShapeFromDxfString.js.map